import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { ChiesiImpostoPolicy } from './chiesiImpostoPolicy';
import { ImpostoNfAccessPorFornecedorPolicy } from './impostoNfAccessPorFornecedorPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';
import { CAMPO_IMPOSTO_NF_CONTA_DIRETA_GRUPO, ehImpostoNfContaDireta } from './impostoNfContaDiretaPolicy';

const momentos = ['PreEvento', 'Negociado', 'Fechamento'];
const isLinhaFisica = (linha = {}) => String(linha?.produto?.codigo ?? '').trim() === '1945';
const numero = (valor) => {
    if (typeof valor === 'number') return valor;
    const texto = String(valor ?? '').trim();
    return Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
};

// O campo já existente é compartilhado pelas três etapas. Zero é uma escolha
// explícita; apenas ausência/vazio usa a alíquota padrão, sem novo schema.
const percentual = (valor) => {
    const p = valor === null || valor === undefined || String(valor).trim() === '' ? 19.05 : numero(valor);
    return Number.isFinite(p) && p >= 0 && p < 100 ? p : null;
};

const resolver = (grupo = {}, clienteEhChiesi = false) => {
    // A linha 1945 do grupo AGÊNCIA calcula para TODOS os clientes; o flag
    // Chiesi passou a escolher apenas a base (serviços próprios Axis).
    if (!AgenciaOrcamentoPolicy.isGrupoAgencia(grupo)) return null;
    const linhas = (grupo.subitem || []).filter(isLinhaFisica);
    if (!linhas.length) return null;
    const p = percentual(linhas[0].impostoPorcentagemItem);
    return {
        linhas, linha: linhas[0], percentual: p,
        erro: linhas.length > 1
            ? 'Mantenha apenas uma linha 1945 - Imposto no grupo AGÊNCIA.'
            : p === null ? 'Informe um percentual de Imposto na AGÊNCIA entre 0 e menor que 100.' : null,
    };
};

const marcar = (grupos = [], clienteEhChiesi = false) => {
    grupos.forEach((grupo) => {
        const regra = resolver(grupo, clienteEhChiesi);
        (grupo.subitem || []).forEach((linha) => {
            // D-0112: sem 1945 manual válida o grupo não manda na conta da NF (sincronizar marca quando manda;
            // apagar só aqui mantém o ciclo idempotente — a chave não muda de posição a cada sincronização).
            if (!regra || regra.erro) delete linha[CAMPO_IMPOSTO_NF_CONTA_DIRETA_GRUPO];
            if (regra && isLinhaFisica(linha)) {
                linha.impostoNfAccessManualAgencia = true;
                linha.inserirCalculoTotalPlanilha = false;
                linha.compoeFee = 'nao';
                linha.nomeCompoeFee = 'Não';
            } else if (linha.impostoNfAccessManualAgencia) linha.impostoNfAccessManualAgencia = false;
        });
    });
};

const sincronizar = (grupos = [], clienteEhChiesi = false, fornecedorAxis = null) => {
    marcar(grupos, clienteEhChiesi);
    grupos.forEach((grupo) => {
        const regra = resolver(grupo, clienteEhChiesi);
        // Não apagar preço persistido nem escolher/apagar IDs em caso ambíguo.
        // A validação bloqueia salvar, e a presença física impede NF virtual extra.
        if (!regra || regra.erro) return;
        const linha = regra.linha;
        const contaDireta = ehImpostoNfContaDireta(linha);
        (grupo.subitem || []).forEach((outra) => { outra[CAMPO_IMPOSTO_NF_CONTA_DIRETA_GRUPO] = contaDireta; });
        linha.inserirCalculoTotalPlanilha = false;
        linha.compoeFee = 'nao';
        linha.nomeCompoeFee = 'Não';
        momentos.forEach((momento) => {
            const base = (grupo.subitem || []).reduce((total, subitem) => {
                // Chiesi: só serviços próprios (fee + Axis). Demais clientes:
                // todas as linhas do grupo exceto o próprio imposto — a
                // alíquota da 1945 incide sobre o Fee Agência composto.
                const entraNaBase = clienteEhChiesi
                    ? ChiesiImpostoPolicy.isServicoProprio(subitem, fornecedorAxis)
                    : !isLinhaFisica(subitem);
                // Linha que já cobra o % Imposto NF dela (ex.: FEE com gross-up próprio) não
                // volta para a base da 1945 — mesma regra da API (normalizarImpostoManualAgenciaChiesi).
                if (!entraNaBase || ehSomenteVisualizacao(subitem) || ImpostoNfAccessPorFornecedorPolicy.temImpostoNfProprio(subitem, fornecedorAxis)) return total;
                return total + (numero(subitem[`total${momento}`]) || 0);
            }, 0);
            const aliquota = regra.percentual / 100;
            // D-0112: 1945 marcada na conta direta (planilha antiga) = base × %; padrão, por fora.
            const divisor = contaDireta ? 1 : (1 - aliquota);
            const imposto = base > 0 ? Math.round((base * aliquota / divisor + Number.EPSILON) * 100) / 100 : 0;
            linha[`quantidade${momento}`] = '1';
            linha[`diaria${momento}`] = '1';
            linha[`servico${momento}`] = '0';
            linha[`valorUnitario${momento}`] = imposto.toFixed(2);
            linha[`total${momento}`] = imposto.toFixed(2);
        });
    });
};

const validar = (grupos = [], clienteEhChiesi = false) => grupos
    .map((grupo) => resolver(grupo, clienteEhChiesi)?.erro).find(Boolean) || null;

export const ChiesiAgenciaImpostoPolicy = { resolver, marcar, sincronizar, validar, percentual };
